import { seededRandom, shade } from '../pixel.js';
import type { PixelCanvas, Point } from '../pixel.js';
import {
  HH,
  HW,
  box,
  drawBox,
  drawBoxes,
  drawCylinder,
  faceQuad,
  faces,
  frontFace,
  iso,
  seatWithBack,
} from './iso.js';
import type { BoxPiece, FurniDrawer } from './iso.js';

/** Drawers for the original kopitiam furniture set. */

/* -------------------------------------------------------------------------- */
/* Items                                                                      */
/* -------------------------------------------------------------------------- */

interface CounterTheme {
  body: string;
  trim: string;
  trimLight: string;
  tileFront: string;
  tileFrontEdge: string;
  tileBack: string;
  tileBackEdge: string;
}

const COUNTER_THEMES: Record<string, CounterTheme> = {
  teal: {
    body: '#2f8f83',
    trim: '#b98a5c',
    trimLight: '#d9ae7e',
    tileFront: '#56b3a4',
    tileFrontEdge: '#1f5f58',
    tileBack: '#3f978a',
    tileBackEdge: '#1a524c',
  },
  white: {
    body: '#e8e4da',
    trim: '#8a8378',
    trimLight: '#aba499',
    tileFront: '#f5f2ea',
    tileFrontEdge: '#c9c3b5',
    tileBack: '#d9d4c8',
    tileBackEdge: '#a8a296',
  },
  black: {
    body: '#2a2a2e',
    trim: '#5a5a60',
    trimLight: '#7a7a80',
    tileFront: '#44444a',
    tileFrontEdge: '#15151a',
    tileBack: '#34343a',
    tileBackEdge: '#101014',
  },
};

function counterDrawer(theme: CounterTheme): FurniDrawer {
  return (pc, rotation) => {
    const body = faces(theme.body);
    drawBox(pc, box(-0.46, 0.46, -0.46, 0.46, 0, 30, body));
    drawBox(pc, box(-0.5, 0.5, -0.5, 0.5, 30, 34, faces(theme.trim, theme.trimLight)));

    // The tiled serving face only faces the customer side; the back stays plain.
    const face = frontFace(rotation) ?? 'left';
    faceQuad(pc, face, 0.46, -0.46, 0.46, 12, 16, theme.tileFront);
    faceQuad(pc, face, 0.46, -0.46, 0.46, 0, 3, theme.tileFrontEdge);
    const otherFace = face === 'left' ? 'right' : 'left';
    faceQuad(pc, otherFace, 0.46, -0.46, 0.46, 12, 16, theme.tileBack);
    faceQuad(pc, otherFace, 0.46, -0.46, 0.46, 0, 3, theme.tileBackEdge);

    // A cup and a toast plate waiting on top — their layout follows the counter's facing.
    const flip = rotation === 4 || rotation === 6;
    const cup = iso(flip ? 0.1 : -0.15, flip ? -0.15 : -0.1, 34);
    pc.rect(cup.x - 2, cup.y - 5, 5, 5, '#f7f3ea');
    pc.rect(cup.x - 2, cup.y - 5, 5, 1, '#a8744c');
    pc.rect(cup.x + 3, cup.y - 4, 1, 2, '#dcd5c6');
    const plate = iso(flip ? -0.18 : 0.18, flip ? 0.1 : 0.12, 34);
    pc.ellipse(plate.x, plate.y - 1, 6, 2.5, '#f2efe8');
    pc.rect(plate.x - 3, plate.y - 3, 6, 2, '#e3b36b');
    pc.rect(plate.x - 3, plate.y - 2, 6, 1, '#6aa84f');
  };
}

function drawCoffeeMachine(pc: PixelCanvas, rotation: number): void {
  drawBox(pc, box(-0.44, 0.44, -0.44, 0.44, 0, 24, faces('#a9b0b7', '#c7cdd2')));
  drawBox(pc, box(-0.3, 0.3, -0.3, 0.3, 24, 52, faces('#d24039')));
  drawBox(pc, box(-0.32, 0.32, -0.32, 0.32, 52, 55, faces('#8b9299')));

  const face = frontFace(rotation) ?? 'left';
  const at = 0.3;
  faceQuad(pc, face, at, -0.16, 0.16, 28, 44, '#2f2f38');
  faceQuad(pc, face, at, -0.04, 0.04, 38, 44, '#c7cdd2');
  // Pressure gauge.
  const gauge = face === 'left' ? iso(0.12, at, 48) : iso(at, -0.12, 48);
  pc.ellipse(gauge.x, gauge.y, 2.5, 2.5, '#f4f1e8');
  pc.px(gauge.x, gauge.y - 1, '#2f2f38');
  // A cup under the spout.
  const cup = face === 'left' ? iso(0, at + 0.02, 28) : iso(at + 0.02, 0, 28);
  pc.rect(cup.x - 2, cup.y - 5, 5, 5, '#f7f3ea');
  // Steam wisps.
  const top = iso(0, 0, 58);
  pc.px(top.x - 2, top.y - 2, '#ffffffcc');
  pc.px(top.x - 1, top.y - 4, '#ffffffcc');
  pc.px(top.x + 1, top.y - 6, '#ffffffaa');
}

interface TableTheme {
  base: readonly [string, string];
  stem: readonly [string, string];
  top: readonly [string, string];
  speckleA: string;
  speckleB: string;
}

const TABLE_THEMES: Record<string, TableTheme> = {
  marble: { base: ['#4a4a52', '#34343c'], stem: ['#4a4a52', '#3a3a42'], top: ['#efebe2', '#4a4a52'], speckleA: '#cfc9bc', speckleB: '#dcd7cb' },
  white: { base: ['#c7c7c2', '#a8a8a2'], stem: ['#c7c7c2', '#b5b5b0'], top: ['#f7f4ec', '#c7c7c2'], speckleA: '#e3ded0', speckleB: '#efebe0' },
  black: { base: ['#3a3a3e', '#202024'], stem: ['#3a3a3e', '#28282c'], top: ['#2a2a2e', '#3a3a3e'], speckleA: '#5a5a60', speckleB: '#6a6a70' },
};

function roundTableDrawer(theme: TableTheme): FurniDrawer {
  return (pc) => {
    drawCylinder(pc, 0.2, 0, 2, theme.base[0], theme.base[1]);
    drawCylinder(pc, 0.05, 2, 26, theme.stem[0], theme.stem[1]);
    drawCylinder(pc, 0.44, 26, 30, theme.top[0], theme.top[1]);

    // Marble speckles on the top face.
    const rand = seededRandom(7);
    const rx = 0.44 * HW * Math.SQRT2;
    const ry = 0.44 * HH * Math.SQRT2;
    for (let i = 0; i < 28; i += 1) {
      const a = rand() * Math.PI * 2;
      const d = Math.sqrt(rand()) * 0.85;
      pc.px(Math.cos(a) * rx * d, -30 + Math.sin(a) * ry * d, rand() > 0.5 ? theme.speckleA : theme.speckleB);
    }
  };
}

function drawStool(pc: PixelCanvas): void {
  const leg = '#8a5530';
  for (const [u, v] of [
    [-0.13, -0.13],
    [0.13, -0.13],
    [0.13, 0.13],
    [-0.13, 0.13],
  ] as const) {
    const a = iso(u, v, 15);
    const b = iso(u * 1.45, v * 1.45, 0);
    pc.line(a.x, a.y, b.x, b.y, leg);
    pc.line(a.x + 1, a.y, b.x + 1, b.y, shade(leg, 0.8));
  }
  // Foot ring.
  const ring = iso(0, 0, 6);
  pc.rect(ring.x - 9, ring.y, 18, 1, leg);
  drawCylinder(pc, 0.22, 14, 17, '#cf8f58', '#a86a3a');
}

interface ChairTheme {
  plasticBase: string;
  plasticTop: string;
  legBase: string;
  backBase: string;
  backTop: string;
}

const CHAIR_THEMES: Record<string, ChairTheme> = {
  red: { plasticBase: '#d9433b', plasticTop: '#ea5a4f', legBase: '#b8352e', backBase: '#d24039', backTop: '#ea5a4f' },
  blue: { plasticBase: '#3a6fd6', plasticTop: '#5a8fe8', legBase: '#2a4fa0', backBase: '#2f5fc0', backTop: '#5a8fe8' },
  black: { plasticBase: '#2b2b30', plasticTop: '#45454c', legBase: '#1a1a1e', backBase: '#242428', backTop: '#45454c' },
};

function chairDrawer(theme: ChairTheme): FurniDrawer {
  return (pc, rotation) => {
    const plastic = faces(theme.plasticBase, theme.plasticTop);
    const legs: BoxPiece[] = [
      [-0.26, -0.26],
      [0.2, -0.26],
      [0.2, 0.2],
      [-0.26, 0.2],
    ].map(([u, v]) => box(u!, u! + 0.06, v!, v! + 0.06, 0, 14, faces(theme.legBase)));
    drawBoxes(pc, legs);
    drawBoxes(pc, seatWithBack(rotation, 0.28, [14, 17], 38, 0.07, plastic, faces(theme.backBase, theme.backTop)));
  };
}

interface ArmchairTheme {
  fabricBase: string;
  fabricTop: string;
  accentBase: string;
  accentTop: string;
}

const ARMCHAIR_THEMES: Record<string, ArmchairTheme> = {
  red: { fabricBase: '#9b3b4a', fabricTop: '#b85466', accentBase: '#8a3140', accentTop: '#a8475a' },
  green: { fabricBase: '#2f6b3f', fabricTop: '#4a8f5a', accentBase: '#255530', accentTop: '#3f7a4a' },
  navy: { fabricBase: '#243a66', fabricTop: '#3a5a95', accentBase: '#1c2e52', accentTop: '#304a7a' },
};

function armchairDrawer(theme: ArmchairTheme): FurniDrawer {
  return (pc, rotation) => {
    const fabric = faces(theme.fabricBase, theme.fabricTop);
    const accent = faces(theme.accentBase, theme.accentTop);
    const pieces = seatWithBack(rotation, 0.42, [0, 14], 36, 0.14, fabric, accent);
    // Armrests run perpendicular to the backrest.
    if (rotation === 2 || rotation === 6) {
      pieces.push(box(-0.42, 0.42, -0.42, -0.3, 14, 24, accent), box(-0.42, 0.42, 0.3, 0.42, 14, 24, accent));
    } else {
      pieces.push(box(-0.42, -0.3, -0.42, 0.42, 14, 24, accent), box(0.3, 0.42, -0.42, 0.42, 14, 24, accent));
    }
    drawBoxes(pc, pieces);
  };
}

interface BeanBagTheme {
  shadow: string;
  mid: string;
  light: string;
  accent: string;
  highlight: string;
}

const BEAN_BAG_THEMES: Record<string, BeanBagTheme> = {
  orange: { shadow: '#b85c1b', mid: '#e07b2e', light: '#f09a4f', accent: '#c9691f', highlight: '#ffc48a' },
  green: { shadow: '#2f6b2a', mid: '#4a9e3f', light: '#6ec25a', accent: '#3a8534', highlight: '#b8e8a0' },
  yellow: { shadow: '#b8900f', mid: '#e8bf2e', light: '#f5d96a', accent: '#d1a21f', highlight: '#fff0a0' },
};

function beanBagDrawer(theme: BeanBagTheme): FurniDrawer {
  return (pc) => {
    pc.ellipse(0, -7, 19, 9, theme.shadow);
    pc.ellipse(-1, -13, 17, 11, theme.mid);
    pc.ellipse(-4, -19, 10, 6, theme.light);
    pc.ellipse(4, -15, 8, 4, theme.accent);
    pc.ellipse(8, -9, 6, 5, theme.accent);
    pc.px(-9, -21, theme.highlight);
    pc.px(-8, -22, theme.highlight);
  };
}

function drawPlant(pc: PixelCanvas): void {
  drawCylinder(pc, 0.18, 0, 16, '#4a3020', '#c0643a');
  const rim = iso(0, 0, 16);
  pc.rect(rim.x - 12, rim.y - 1, 24, 2, '#d8774a');
  pc.rect(-1, -44, 3, 28, '#7a5230');
  pc.rect(0, -44, 1, 28, '#95683f');

  const dark = '#2f7f3c';
  const mid = '#3d9e4c';
  const light = '#5cbd63';
  const leaves: Array<[Point[], string]> = [
    [[{ x: 0, y: -44 }, { x: -22, y: -40 }, { x: -26, y: -32 }, { x: -8, y: -41 }], dark],
    [[{ x: 0, y: -44 }, { x: 22, y: -40 }, { x: 26, y: -31 }, { x: 8, y: -41 }], dark],
    [[{ x: 0, y: -45 }, { x: -18, y: -52 }, { x: -22, y: -48 }, { x: -6, y: -44 }], mid],
    [[{ x: 0, y: -45 }, { x: 18, y: -53 }, { x: 23, y: -49 }, { x: 6, y: -44 }], mid],
    [[{ x: 0, y: -45 }, { x: -6, y: -60 }, { x: -2, y: -62 }, { x: 3, y: -47 }], light],
    [[{ x: 0, y: -45 }, { x: 10, y: -58 }, { x: 14, y: -57 }, { x: 4, y: -45 }], light],
  ];
  for (const [points, color] of leaves) pc.poly(points, color);
}

function drawLamp(pc: PixelCanvas): void {
  drawCylinder(pc, 0.16, 0, 3, '#4a4a52', '#34343c');
  pc.rect(-1, -60, 2, 58, '#5a5a62');
  pc.rect(-1, -60, 1, 58, '#7a7a84');
  pc.poly(
    [
      { x: -11, y: -58 },
      { x: 11, y: -58 },
      { x: 7, y: -74 },
      { x: -7, y: -74 },
    ],
    '#f3e2b3',
  );
  pc.poly(
    [
      { x: 3, y: -58 },
      { x: 11, y: -58 },
      { x: 7, y: -74 },
      { x: 2, y: -74 },
    ],
    '#dcc58f',
  );
  pc.rect(-9, -58, 18, 2, '#ffe58a');
}

function drawRug(pc: PixelCanvas): void {
  const diamond = (r: number, color: string) =>
    pc.poly([iso(-r, -r), iso(r, -r), iso(r, r), iso(-r, r)], color);
  diamond(0.47, '#9e3f2e');
  diamond(0.42, '#c8573f');
  diamond(0.32, '#e8c26b');
  diamond(0.22, '#c8573f');
  diamond(0.1, '#2f8f83');
  for (const [u, v] of [
    [0.37, 0],
    [-0.37, 0],
    [0, 0.37],
    [0, -0.37],
  ] as const) {
    const p = iso(u, v);
    pc.rect(p.x - 1, p.y, 2, 1, '#f4e3b0');
  }
}

const BOOK_COLORS = ['#d9433b', '#3a6fd6', '#3d9e4c', '#f0bf36', '#8a52c8', '#efeae0', '#ef7d2c'];

function drawBookshelf(pc: PixelCanvas, rotation: number): void {
  const wood = faces('#8a5a34', '#a8703f');
  const face = frontFace(rotation);
  const alongV = rotation === 2 || rotation === 6;
  const piece = alongV
    ? box(-0.2, 0.2, -0.46, 0.46, 0, 66, wood)
    : box(-0.46, 0.46, -0.2, 0.2, 0, 66, wood);
  drawBox(pc, piece);
  if (!face) return;

  const at = 0.2;
  faceQuad(pc, face, at, -0.4, 0.4, 3, 62, '#3b2415');
  const rand = seededRandom(rotation + 11);
  for (const base of [4, 24, 44]) {
    faceQuad(pc, face, at, -0.42, 0.42, base - 2, base, '#b17a48');
    let s = -0.38;
    while (s < 0.34) {
      const width = 0.05 + rand() * 0.03;
      const height = 12 + Math.floor(rand() * 5);
      const color = BOOK_COLORS[Math.floor(rand() * BOOK_COLORS.length)]!;
      faceQuad(pc, face, at, s, s + width, base, base + height, color);
      s += width + 0.012;
    }
  }
  faceQuad(pc, face, at, -0.42, 0.42, 62, 66, '#b17a48');
}

function drawJukebox(pc: PixelCanvas, rotation: number): void {
  drawBox(pc, box(-0.36, 0.36, -0.36, 0.36, 0, 40, faces('#c8423b', '#de5a50')));
  drawBox(pc, box(-0.3, 0.3, -0.3, 0.3, 40, 50, faces('#f0bf36', '#ffd966')));
  const face = frontFace(rotation) ?? 'left';
  const at = 0.36;
  faceQuad(pc, face, at, -0.26, 0.26, 4, 14, '#3a2a24');
  for (let s = -0.22; s < 0.24; s += 0.08) faceQuad(pc, face, at, s, s + 0.03, 5, 13, '#5a463c');
  faceQuad(pc, face, at, -0.24, 0.24, 18, 36, '#ffd24a');
  faceQuad(pc, face, at, -0.2, 0.2, 21, 33, '#ff9d3a');
  faceQuad(pc, face, at, -0.08, 0.08, 23, 31, '#fff2b8');
  const colors = ['#ff5a8a', '#5ad1ff', '#9dff5a', '#ffe15a'];
  colors.forEach((color, i) => {
    const s = -0.2 + i * 0.13;
    faceQuad(pc, face, 0.3, s, s + 0.06, 43, 47, color);
  });
}

function drawArcade(pc: PixelCanvas, rotation: number): void {
  drawBox(pc, box(-0.34, 0.34, -0.34, 0.34, 0, 60, faces('#2b3a8f', '#3a4cb0')));
  const face = frontFace(rotation) ?? 'left';
  const at = 0.34;
  faceQuad(pc, face, at, -0.3, 0.3, 52, 58, '#f0bf36');
  faceQuad(pc, face, at, -0.22, 0.22, 30, 48, '#0f1830');
  // A tiny invader on screen.
  const invader = ['0100010', '0011100', '0111110', '1101011', '1111111', '0100010'];
  invader.forEach((row, ry) => {
    [...row].forEach((bit, rx) => {
      if (bit !== '1') return;
      const s = -0.1 + rx * 0.03;
      faceQuad(pc, face, at, s, s + 0.03, 44 - ry * 2, 46 - ry * 2, '#6dff8a');
    });
  });
  faceQuad(pc, face, at, -0.3, 0.3, 22, 27, '#20285f');
  faceQuad(pc, face, at, -0.16, -0.1, 23, 26, '#ff4a4a');
  faceQuad(pc, face, at, 0.02, 0.08, 23, 26, '#ffd24a');
  faceQuad(pc, face, at, 0.12, 0.18, 23, 26, '#5ad1ff');
}

function drawFan(pc: PixelCanvas): void {
  drawCylinder(pc, 0.18, 0, 3, '#e8e4da', '#c9c3b5');
  pc.rect(-1, -44, 3, 42, '#d6d0c4');
  pc.rect(1, -44, 1, 42, '#b5ae9f');
  pc.ellipse(0, -50, 12, 12, '#c9d6de');
  pc.ellipse(0, -50, 10, 10, '#eef4f7');
  pc.ellipse(-3, -53, 4, 3, '#7fb7d9');
  pc.ellipse(3, -47, 4, 3, '#7fb7d9');
  pc.ellipse(4, -54, 3, 3, '#6aa6cc');
  pc.ellipse(-4, -46, 3, 3, '#6aa6cc');
  pc.ellipse(0, -50, 2, 2, '#e8e4da');
  pc.line(-10, -50, 10, -50, '#b9c7d0');
  pc.line(0, -60, 0, -40, '#b9c7d0');
}



export const CORE_DRAWERS: Record<string, FurniDrawer> = {
  counter: counterDrawer(COUNTER_THEMES.teal!),
  counter_white: counterDrawer(COUNTER_THEMES.white!),
  counter_black: counterDrawer(COUNTER_THEMES.black!),
  coffee_machine: drawCoffeeMachine,
  table_round: roundTableDrawer(TABLE_THEMES.marble!),
  table_round_white: roundTableDrawer(TABLE_THEMES.white!),
  table_round_black: roundTableDrawer(TABLE_THEMES.black!),
  stool: drawStool,
  chair: chairDrawer(CHAIR_THEMES.red!),
  chair_blue: chairDrawer(CHAIR_THEMES.blue!),
  chair_black: chairDrawer(CHAIR_THEMES.black!),
  armchair: armchairDrawer(ARMCHAIR_THEMES.red!),
  armchair_green: armchairDrawer(ARMCHAIR_THEMES.green!),
  armchair_navy: armchairDrawer(ARMCHAIR_THEMES.navy!),
  bean_bag: beanBagDrawer(BEAN_BAG_THEMES.orange!),
  bean_bag_green: beanBagDrawer(BEAN_BAG_THEMES.green!),
  bean_bag_yellow: beanBagDrawer(BEAN_BAG_THEMES.yellow!),
  plant: drawPlant,
  lamp: drawLamp,
  rug: drawRug,
  bookshelf: drawBookshelf,
  jukebox: drawJukebox,
  arcade: drawArcade,
  fan: drawFan,
};

